
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  PieChart as PieChartIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Public as PublicIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
  Work as WorkIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";
// Get most recent data from filtered activities (by year or all)
const getMostRecentData = (activities) => {
  const investorMap = new Map();
  
  activities.forEach(activity => {
    const investorId = activity.investor_id;
    const existing = investorMap.get(investorId);
    
    if (!existing) {
      investorMap.set(investorId, { ...activity });
    } else {
      // Compare by activity_period_to (end date)
      const existingDate = new Date(existing.activity_period_to || existing.recorded_on || 0);
      const currentDate = new Date(activity.activity_period_to || activity.recorded_on || 0);
      
      if (currentDate > existingDate) {
        investorMap.set(investorId, { ...activity });
      }
    }
  });
  
  return Array.from(investorMap.values());
};

// Get summary statistics from filtered activities (using most recent data)
const getSummaryStatsFromFiltered = (filteredActivities) => {
  // Get most recent data from filtered activities
  const mostRecentData = getMostRecentData(filteredActivities);
  
  const totalLaborFemale = mostRecentData.reduce((sum, a) => sum + (a.emp_labor_female || 0), 0);
  const totalLaborMale = mostRecentData.reduce((sum, a) => sum + (a.emp_labor_male || 0), 0);
  const totalProfFemale = mostRecentData.reduce((sum, a) => sum + (a.emp_prof_female || 0), 0);
  const totalProfMale = mostRecentData.reduce((sum, a) => sum + (a.emp_prof_male || 0), 0);
  
  const totalLabor = totalLaborFemale + totalLaborMale;
  const totalProfessional = totalProfFemale + totalProfMale;
  const totalEmployees = totalLabor + totalProfessional;
  
  const uniqueInvestors = new Set(mostRecentData.map(a => a.investor_id)).size;
  
  return {
    totalLaborFemale,
    totalLaborMale,
    totalProfFemale,
    totalProfMale,
    totalLabor,
    totalProfessional,
    totalEmployees,
    totalActivities: mostRecentData.length,
    uniqueInvestors
  };
};
// Helper function to safely parse number from various formats
const safeParseNumber = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/,/g, '').trim();
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};
//****************************** */
// Function to get most recent activity per investor
const getMostRecentPerInvestor = (activities) => {
  const investorMap = new Map();
  
  activities.forEach(activity => {
    const investorId = activity.investor_id;
    const existing = investorMap.get(investorId);
    
    if (!existing) {
      investorMap.set(investorId, { ...activity });
    } else {
      // Compare by activity_period_to (end date) or recorded_on
      const existingDate = new Date(existing.activity_period_to || existing.recorded_on || 0);
      const currentDate = new Date(activity.activity_period_to || activity.recorded_on || 0);
      
      if (currentDate > existingDate) {
        investorMap.set(investorId, { ...activity });
      }
    }
  });
  
  return Array.from(investorMap.values());
};
//****************************** */
// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employment-tabpanel-${index}`}
      aria-labelledby={`employment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Bar Chart Component
const BarChart = ({ title, data, dataKey, color, formatValue, xAxisKey = "name" }) => {
  const items = Object.keys(data);
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(data).map(d => d[dataKey] || 0), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(50, Math.max(30, 80 - items.length * 2));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2.5, 
        minHeight: 400, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {items.map((item) => {
          const value = data[item][dataKey] || 0;
          const height = maxValue > 0 ? (value / maxValue) * 280 : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={item} 
              title={`${item}: ${formatValue(value)} (${percentage}% of max)`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: height,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -22, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: height > 35 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {height > 35 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5, 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#333'
                  }}
                >
                  {item.length > 20 ? item.substring(0, 18) + '...' : item}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#888',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total {title}: {formatValue(Object.values(data).reduce((sum, d) => sum + (d[dataKey] || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Pie Chart Component
const PieChart = ({ title, data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + (data[key] || 0), 0);
  const colors = ['#4caf50', '#ff9800', '#e91e63', '#2196f3', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c', '#f44336'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  const centerX = 120, centerY = 120, radius = 90;
  const svgSize = 280;
  
  const getPercentage = (value) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2, textAlign: 'center' }}>
        {title} - Total: {formatValue(total)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = data[key] || 0;
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            <circle cx={centerX} cy={centerY} r="25" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 200, maxHeight: 250, overflowY: 'auto' }}>
          {items.map((key, index) => {
            const value = data[key] || 0;
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {key.length > 20 ? key.substring(0, 18) + '...' : key} ({percentage}%)
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length] }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Total Items: {items.length}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Total Value: {formatValue(total)}</Typography>
      </Box>
    </Box>
  );
};

// Stacked Bar Chart for Gender Comparison
const GenderStackedBarChart = ({ title, parkData }) => {
  const parks = Object.keys(parkData).sort();
  
  if (parks.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(parkData).map(d => 
    (d.totalLaborFemale || 0) + (d.totalLaborMale || 0)
  ), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No employment data available</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(40, Math.max(25, 60 - parks.length));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 3, 
        minHeight: 450, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {parks.map((park) => {
          const femaleValue = parkData[park]?.totalLaborFemale || 0;
          const maleValue = parkData[park]?.totalLaborMale || 0;
          const totalValue = femaleValue + maleValue;
          const heightFemale = maxValue > 0 ? (femaleValue / maxValue) * 280 : 0;
          const heightMale = maxValue > 0 ? (maleValue / maxValue) * 280 : 0;
          const femalePercentage = totalValue > 0 ? (femaleValue / totalValue) * 100 : 0;
          const malePercentage = totalValue > 0 ? (maleValue / totalValue) * 100 : 0;
          
          return (
            <Tooltip 
              key={park} 
              title={
                <Box>
                  <Typography variant="subtitle2">{park}</Typography>
                  <Typography variant="body2">Female: {formatNumber(femaleValue)} ({femalePercentage.toFixed(1)}%)</Typography>
                  <Typography variant="body2">Male: {formatNumber(maleValue)} ({malePercentage.toFixed(1)}%)</Typography>
                  <Typography variant="body2">Total: {formatNumber(totalValue)}</Typography>
                </Box>
              } 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 90,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'flex-end', height: 320 }}>
                  <Box sx={{ 
                    position: 'relative',
                    height: heightFemale,
                    width: `${barWidth}px`,
                    bgcolor: '#e91e63',
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.3s ease',
                    opacity: 0.85,
                    '&:hover': {
                      opacity: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    {heightFemale > 30 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: heightFemale - 20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        {formatNumber(femaleValue)}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ 
                    position: 'relative',
                    height: heightMale,
                    width: `${barWidth}px`,
                    bgcolor: '#2196f3',
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.3s ease',
                    opacity: 0.85,
                    '&:hover': {
                      opacity: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    {heightMale > 30 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: heightMale - 20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        {formatNumber(maleValue)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1, 
                    fontWeight: 'bold', 
                    fontSize: '0.7rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#555'
                  }}
                >
                  {park.length > 20 ? park.substring(0, 18) + '...' : park}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: '#e91e63', borderRadius: 1 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>Female</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: '#2196f3', borderRadius: 1 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>Male</Typography>
                  </Box>
                </Box>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#666',
                    fontSize: '0.65rem',
                    mt: 0.5,
                    fontWeight: 'bold'
                  }}
                >
                  Total: {formatNumber(totalValue)}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total Parks: {parks.length} | Total Employment: {formatNumber(Object.values(parkData).reduce((sum, d) => sum + (d.totalLaborFemale || 0) + (d.totalLaborMale || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Professional vs Labor Chart
const ProfessionalVsLaborChart = ({ title, parkData }) => {
  const parks = Object.keys(parkData).sort();
  
  if (parks.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(parkData).map(d => 
    Math.max((d.totalLabor || 0), (d.totalProfessional || 0))
  ), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No employment data available</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(35, Math.max(25, 55 - parks.length));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 3, 
        minHeight: 450, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {parks.map((park) => {
          const laborValue = parkData[park]?.totalLabor || 0;
          const profValue = parkData[park]?.totalProfessional || 0;
          const heightLabor = maxValue > 0 ? (laborValue / maxValue) * 280 : 0;
          const heightProf = maxValue > 0 ? (profValue / maxValue) * 280 : 0;
          const totalValue = laborValue + profValue;
          const laborPercentage = totalValue > 0 ? (laborValue / totalValue) * 100 : 0;
          const profPercentage = totalValue > 0 ? (profValue / totalValue) * 100 : 0;
          
          return (
            <Tooltip 
              key={park} 
              title={
                <Box>
                  <Typography variant="subtitle2">{park}</Typography>
                  <Typography variant="body2">Labor: {formatNumber(laborValue)} ({laborPercentage.toFixed(1)}%)</Typography>
                  <Typography variant="body2">Professional: {formatNumber(profValue)} ({profPercentage.toFixed(1)}%)</Typography>
                  <Typography variant="body2">Total: {formatNumber(totalValue)}</Typography>
                </Box>
              } 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 90,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'flex-end', height: 320 }}>
                  <Box sx={{ 
                    position: 'relative',
                    height: heightLabor,
                    width: `${barWidth}px`,
                    bgcolor: '#4caf50',
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.3s ease',
                    opacity: 0.85,
                    '&:hover': {
                      opacity: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    {heightLabor > 30 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: heightLabor - 20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        {formatNumber(laborValue)}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ 
                    position: 'relative',
                    height: heightProf,
                    width: `${barWidth}px`,
                    bgcolor: '#ff9800',
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.3s ease',
                    opacity: 0.85,
                    '&:hover': {
                      opacity: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    {heightProf > 30 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: heightProf - 20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        {formatNumber(profValue)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1, 
                    fontWeight: 'bold', 
                    fontSize: '0.7rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#555'
                  }}
                >
                  {park.length > 20 ? park.substring(0, 18) + '...' : park}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: '#4caf50', borderRadius: 1 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>Labor</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: '#ff9800', borderRadius: 1 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>Professional</Typography>
                  </Box>
                </Box>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#666',
                    fontSize: '0.65rem',
                    mt: 0.5,
                    fontWeight: 'bold'
                  }}
                >
                  Total: {formatNumber(totalValue)}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total Parks: {parks.length} | Total Labor: {formatNumber(Object.values(parkData).reduce((sum, d) => sum + (d.totalLabor || 0), 0))} | Total Professional: {formatNumber(Object.values(parkData).reduce((sum, d) => sum + (d.totalProfessional || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Yearly Trend Line Chart
const YearlyTrendLineChart = ({ title, yearlyData, dataKeys, colors, formatValue }) => {
  const years = Object.keys(yearlyData).sort((a, b) => a - b);
  
  if (years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No yearly data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(yearlyData).flatMap(d => 
    dataKeys.map(key => d[key] || 0)
  ), 0);
  
  const chartHeight = 350;
  const chartWidth = Math.max(600, years.length * 100);
  const padding = { top: 30, right: 40, bottom: 60, left: 80 };
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {[0, 25, 50, 75, 100].map((percent) => {
          const y = chartHeight - padding.bottom - (percent / 100) * (chartHeight - padding.top - padding.bottom);
          const value = (maxValue * percent) / 100;
          return (
            <g key={percent}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="4" />
              <text x={padding.left - 8} y={y} textAnchor="end" fontSize="10" fill="#666">{formatValue(value)}</text>
            </g>
          );
        })}
        
        {dataKeys.map((dataKey, idx) => (
          <polyline
            key={dataKey}
            points={years.map((year, index) => {
              const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const value = yearlyData[year]?.[dataKey] || 0;
              const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke={colors[idx % colors.length]}
            strokeWidth="2.5"
          />
        ))}
        
        {years.map((year, index) => {
          const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
          
          return (
            <g key={year}>
              {dataKeys.map((dataKey, idx) => {
                const value = yearlyData[year]?.[dataKey] || 0;
                const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
                const color = colors[idx % colors.length];
                
                return (
                  <g key={dataKey}>
                    <circle cx={x} cy={y} r="5" fill={color} stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
                    {value > 0 && (
                      <text 
                        x={x} 
                        y={idx === 0 ? y - 10 : y + 15} 
                        textAnchor="middle" 
                        fontSize="8" 
                        fill={color}
                      >
                        {formatValue(value)}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="11" fontWeight="bold">{year}</text>
            </g>
          );
        })}
        
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        
        <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, 20, ${chartHeight / 2})`}>Number of Employees</text>
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">Year</text>
      </svg>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
        {dataKeys.map((dataKey, idx) => {
          let label = "";
          if (dataKey === "totalLabor") label = "Total Labor";
          else if (dataKey === "totalProfessional") label = "Total Professional";
          else if (dataKey === "totalFemale") label = "Female Employees";
          else if (dataKey === "totalMale") label = "Male Employees";
          else label = dataKey;
          
          return (
            <Box key={dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 3, bgcolor: colors[idx % colors.length], borderRadius: 1 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
            </Box>
          );
        })}
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Years: {years.join(' → ')}</Typography>
      </Box>
    </Box>
  );
};

// Main Employment Report Component
export default function EmploymentReportDashboard() {
  // State Management
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPark, setSelectedPark] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedEmploymentType, setSelectedEmploymentType] = useState("all");
  const [startYear, setStartYear] = useState("all");
  const [endYear, setEndYear] = useState("all");
  const [startMonth, setStartMonth] = useState("all");
  const [endMonth, setEndMonth] = useState("all");
  
  // Chart States
  const [showChart, setShowChart] = useState(false);
  const [chartView, setChartView] = useState("park");
  const [parkChartTab, setParkChartTab] = useState(0);
  const [parkChartType, setParkChartType] = useState('bar'); // ADDED: Chart type toggle state
  
  // Data States
  const [activitiesData, setActivitiesData] = useState([]);
  const [investorsData, setInvestorsData] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  
  // Dynamic filter options
  const [parksList, setParksList] = useState(["all"]);
  const [statusesList, setStatusesList] = useState(["all"]);
  
  // Dialog States
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Generate last 10 years dynamically
  const getLastNYears = (n) => {
    const currentYear = new Date().getFullYear();
    const years = ["all"];
    for (let i = 0; i < n; i++) {
      years.push((currentYear - i).toString());
    }
    return years;
  };
  
  const years = getLastNYears(10);
  
  const months = [
    { value: "all", label: "All Months" },
    { value: "01", label: "January" }, { value: "02", label: "February" },
    { value: "03", label: "March" }, { value: "04", label: "April" },
    { value: "05", label: "May" }, { value: "06", label: "June" },
    { value: "07", label: "July" }, { value: "08", label: "August" },
    { value: "09", label: "September" }, { value: "10", label: "October" },
    { value: "11", label: "November" }, { value: "12", label: "December" }
  ];
  
  const employmentTypes = [
    { value: "all", label: "All Types" },
    { value: "labor", label: "Labor Only" },
    { value: "professional", label: "Professional Only" },
    { value: "both", label: "Both (Labor & Professional)" }
  ];

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch(statusLower) {
      case 'active':
      case 'verified':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'inactive':
        return { bg: '#f5f5f5', color: '#9e9e9e' };
      case 'pending':
      case 'request':
        return { bg: '#fff3e0', color: '#ef6c00' };
      case 'expired':
        return { bg: '#ffebee', color: '#c62828' };
      case 'declined':
        return { bg: '#ffebee', color: '#c62828' };
      default: 
        return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  // Park name mapping
  const parkMap = {
    1: "Bole Lemi Industrial Park",
    2: "Addis Ababa IPDC Park",
    3: "Kilinto Industrial Park",
    4: "Debre Birhan Industrial Park",
    5: "Hawassa Industrial Park",
    6: "Mekelle Industrial Park",
    7: "Dire Dawa Industrial Park",
    8: "ICT Park Addis",
    11: "Park 11",
    13: "Park 13"
  };


// Fetch data from API - Keep ALL data for the table
useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both employment and park_employment data
      const [employmentResponse, parkEmploymentResponse] = await Promise.all([
        GetDataFromApiWithParams(`investors/getemployment`),
        GetDataFromApiWithParams(`investors/getparkemployment`).catch(() => [])
      ]);
      
      console.log("Employment API Response:", employmentResponse);
      console.log("Park Employment Response:", parkEmploymentResponse);
      
      let activities = [];
      let parkEmploymentData = [];
      
      if (employmentResponse && Array.isArray(employmentResponse)) {
        activities = employmentResponse;
      } else if (employmentResponse?.data && Array.isArray(employmentResponse.data)) {
        activities = employmentResponse.data;
      } else if (employmentResponse?.results && Array.isArray(employmentResponse.results)) {
        activities = employmentResponse.results;
      } else if (employmentResponse?.items && Array.isArray(employmentResponse.items)) {
        activities = employmentResponse.items;
      }
      
      if (parkEmploymentResponse && Array.isArray(parkEmploymentResponse)) {
        parkEmploymentData = parkEmploymentResponse;
      } else if (parkEmploymentResponse?.data && Array.isArray(parkEmploymentResponse.data)) {
        parkEmploymentData = parkEmploymentResponse.data;
      }
      
      if (activities.length === 0) {
        console.warn("No employment data received from API");
        setLoading(false);
        return;
      }
      
      // Create a map of park_employment data by investor_id
      const parkEmploymentMap = new Map();
      parkEmploymentData.forEach(item => {
        const key = item.investor_id;
        parkEmploymentMap.set(key, {
          prof_male: safeParseNumber(item.professional_male),
          prof_female: safeParseNumber(item.professional_female),
          labor_male: safeParseNumber(item.labor_male),
          labor_female: safeParseNumber(item.labor_female),
          total_present: safeParseNumber(item.total_present)
        });
      });
      
      let investors = [];
      try {
        const investorsResponse = await GetDataFromApiWithParams(`investors/getinverstor1`);
        if (investorsResponse && Array.isArray(investorsResponse)) {
          investors = investorsResponse;
        } else if (investorsResponse?.data && Array.isArray(investorsResponse.data)) {
          investors = investorsResponse.data;
        }
      } catch (investorError) {
        console.warn("Could not fetch investors data:", investorError);
      }
      
      setInvestorsData(investors);
      
      const transformedActivities = activities.map(activity => {
        const investor = investors.find(i => 
          i.investor_id == activity.investor_id || 
          i.id == activity.investor_id
        );
        
        // Get park_employment values for this investor
        const parkData = parkEmploymentMap.get(activity.investor_id) || {
          prof_male: 0,
          prof_female: 0,
          labor_male: 0,
          labor_female: 0,
          total_present: 0
        };
        
        const empLaborFemale = safeParseNumber(activity.emp_labor_female);
        const empLaborMale = safeParseNumber(activity.emp_labor_male);
        const empProfFemale = safeParseNumber(activity.emp_prof_female);
        const empProfMale = safeParseNumber(activity.emp_prof_male);
        
        // Combine activity employment with park_employment (present values)
        const totalLaborFemale = empLaborFemale + parkData.labor_female;
        const totalLaborMale = empLaborMale + parkData.labor_male;
        const totalProfFemale = empProfFemale + parkData.prof_female;
        const totalProfMale = empProfMale + parkData.prof_male;
        
        const totalLabor = totalLaborFemale + totalLaborMale;
        const totalProfessional = totalProfFemale + totalProfMale;
        const totalEmployees = totalLabor + totalProfessional;
        
        let year = new Date().getFullYear().toString();
        let month = "01";
        
        if (activity.recorded_on) {
          const date = new Date(activity.recorded_on);
          year = date.getFullYear().toString();
          month = (date.getMonth() + 1).toString().padStart(2, '0');
        } else if (activity.activity_period_start_from) {
          const date = new Date(activity.activity_period_start_from);
          year = date.getFullYear().toString();
          month = (date.getMonth() + 1).toString().padStart(2, '0');
        } else if (activity.year) {
          year = activity.year.toString();
        }
        
        let parkName = activity.park_name || "N/A";
        if ((!parkName || parkName === "N/A") && activity.park_id) {
          parkName = parkMap[activity.park_id] || `Park ${activity.park_id}`;
        }
        
        return {
          ...activity,
          investor_id: activity.investor_id || activity.id,
          company_name: investor?.company_name || activity.company_name || "N/A",
          park_name: parkName,
          year: year,
          month: month,
          emp_labor_female: totalLaborFemale,
          emp_labor_male: totalLaborMale,
          emp_prof_female: totalProfFemale,
          emp_prof_male: totalProfMale,
          total_labor: totalLabor,
          total_professional: totalProfessional,
          total_employees: totalEmployees,
          status: activity.status || activity.verification_status || "REQUEST",
          // Keep original values for reference
          _original_emp_labor_female: empLaborFemale,
          _original_emp_labor_male: empLaborMale,
          _original_emp_prof_female: empProfFemale,
          _original_emp_prof_male: empProfMale,
          _park_employment: parkData
        };
      });
      
      console.log("Transformed activities (ALL records):", transformedActivities);
      
      // ✅ KEEP ALL DATA - Don't filter to most recent for the table
      setActivitiesData(transformedActivities);
      setFilteredActivities(transformedActivities);
      
      const uniqueParks = new Set();
      transformedActivities.forEach(activity => {
        if (activity.park_name && activity.park_name !== "N/A") {
          uniqueParks.add(activity.park_name);
        }
      });
      setParksList(["all", ...Array.from(uniqueParks).sort()]);
      
      const uniqueStatuses = new Set();
      transformedActivities.forEach(activity => {
        if (activity.status) {
          uniqueStatuses.add(activity.status);
        }
      });
      setStatusesList(["all", ...Array.from(uniqueStatuses).sort()]);
      
    } catch (error) {
      console.error("Failed to fetch employment data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  fetchData();
}, []);

  // Calculate summary statistics
  // const summaryStats = useMemo(() => {
  //   const totalLaborFemale = filteredActivities.reduce((sum, a) => sum + (a.emp_labor_female || 0), 0);
  //   const totalLaborMale = filteredActivities.reduce((sum, a) => sum + (a.emp_labor_male || 0), 0);
  //   const totalProfFemale = filteredActivities.reduce((sum, a) => sum + (a.emp_prof_female || 0), 0);
  //   const totalProfMale = filteredActivities.reduce((sum, a) => sum + (a.emp_prof_male || 0), 0);
    
  //   const totalLabor = totalLaborFemale + totalLaborMale;
  //   const totalProfessional = totalProfFemale + totalProfMale;
  //   const totalEmployees = totalLabor + totalProfessional;
    
  //   const activeActivities = filteredActivities.filter(a => {
  //     const status = a.status?.toLowerCase();
  //     return status === 'active' || status === 'verified';
  //   }).length;
    
  //   const uniqueInvestors = new Set(filteredActivities.map(a => a.investor_id)).size;
    
  //   return {
  //     totalLaborFemale,
  //     totalLaborMale,
  //     totalProfFemale,
  //     totalProfMale,
  //     totalLabor,
  //     totalProfessional,
  //     totalEmployees,
  //     activeActivities,
  //     totalActivities: filteredActivities.length,
  //     uniqueInvestors
  //   };
  // }, [filteredActivities]);
//*********************************************************** */
// Calculate summary statistics from filtered data (using most recent data)
// Calculate summary statistics from filtered data (using most recent data per investor)
const summaryStats = useMemo(() => {
  // Get most recent data from filtered activities for the summary cards
  const mostRecentData = getMostRecentPerInvestor(filteredActivities);
  
  const totalLaborFemale = mostRecentData.reduce((sum, a) => sum + (a.emp_labor_female || 0), 0);
  const totalLaborMale = mostRecentData.reduce((sum, a) => sum + (a.emp_labor_male || 0), 0);
  const totalProfFemale = mostRecentData.reduce((sum, a) => sum + (a.emp_prof_female || 0), 0);
  const totalProfMale = mostRecentData.reduce((sum, a) => sum + (a.emp_prof_male || 0), 0);
  
  const totalLabor = totalLaborFemale + totalLaborMale;
  const totalProfessional = totalProfFemale + totalProfMale;
  const totalEmployees = totalLabor + totalProfessional;
  
  const activeActivities = mostRecentData.filter(a => {
    const status = a.status?.toLowerCase();
    return status === 'active' || status === 'verified';
  }).length;
  
  const uniqueInvestors = new Set(mostRecentData.map(a => a.investor_id)).size;
  
  return {
    totalLaborFemale,
    totalLaborMale,
    totalProfFemale,
    totalProfMale,
    totalLabor,
    totalProfessional,
    totalEmployees,
    activeActivities,
    totalActivities: mostRecentData.length,
    uniqueInvestors
  };
}, [filteredActivities]);
  // Filter Data with employment type and month filters
  const applyFilters = useCallback(() => {
    let filteredList = [...activitiesData];
    
    if (searchTerm) {
      filteredList = filteredList.filter(item =>
        item.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.investor_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.park_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedPark !== "all") {
      filteredList = filteredList.filter(item => item.park_name === selectedPark);
    }
    
    if (selectedStatus !== "all") {
      filteredList = filteredList.filter(item => 
        item.status?.toLowerCase() === selectedStatus.toLowerCase()
      );
    }
    
    // Employment Type Filter
    if (selectedEmploymentType !== "all") {
      filteredList = filteredList.filter(item => {
        const hasLabor = (item.emp_labor_female > 0 || item.emp_labor_male > 0);
        const hasProfessional = (item.emp_prof_female > 0 || item.emp_prof_male > 0);
        
        if (selectedEmploymentType === "labor") return hasLabor;
        if (selectedEmploymentType === "professional") return hasProfessional;
        if (selectedEmploymentType === "both") return hasLabor && hasProfessional;
        return true;
      });
    }
    
    // Year filter
    if (startYear !== "all" || endYear !== "all") {
      filteredList = filteredList.filter(item => {
        const itemYear = parseInt(item.year);
        const startYearNum = startYear !== "all" ? parseInt(startYear) : -Infinity;
        const endYearNum = endYear !== "all" ? parseInt(endYear) : Infinity;
        return itemYear >= startYearNum && itemYear <= endYearNum;
      });
    }
    
    // Month filter
    if (startMonth !== "all" || endMonth !== "all") {
      filteredList = filteredList.filter(item => {
        const itemMonth = parseInt(item.month);
        const startMonthNum = startMonth !== "all" ? parseInt(startMonth) : 1;
        const endMonthNum = endMonth !== "all" ? parseInt(endMonth) : 12;
        return itemMonth >= startMonthNum && itemMonth <= endMonthNum;
      });
    }
    
    setFilteredActivities(filteredList);
    setPage(0);
  }, [activitiesData, searchTerm, selectedPark, selectedStatus, selectedEmploymentType, startYear, endYear, startMonth, endMonth]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Chart data preparation
  const chartData = useMemo(() => {
    const parkLaborData = {};
    const parkProfessionalData = {};
    const parkGenderData = {};
    const parkEmployeeTypeData = {};
    const yearlyEmploymentData = {};
    
    filteredActivities.forEach(activity => {
      const park = activity.park_name || "Unassigned";
      const year = activity.year;
      
      const laborFemale = activity.emp_labor_female || 0;
      const laborMale = activity.emp_labor_male || 0;
      const profFemale = activity.emp_prof_female || 0;
      const profMale = activity.emp_prof_male || 0;
      
      if (!parkLaborData[park]) {
        parkLaborData[park] = { 
          totalLabor: 0,
          totalLaborFemale: 0,
          totalLaborMale: 0
        };
      }
      parkLaborData[park].totalLabor += laborFemale + laborMale;
      parkLaborData[park].totalLaborFemale += laborFemale;
      parkLaborData[park].totalLaborMale += laborMale;
      
      if (!parkProfessionalData[park]) {
        parkProfessionalData[park] = { 
          totalProfessional: 0,
          totalProfFemale: 0,
          totalProfMale: 0
        };
      }
      parkProfessionalData[park].totalProfessional += profFemale + profMale;
      parkProfessionalData[park].totalProfFemale += profFemale;
      parkProfessionalData[park].totalProfMale += profMale;
      
      if (!parkGenderData[park]) {
        parkGenderData[park] = { 
          totalFemale: 0,
          totalMale: 0,
          totalLaborFemale: 0,
          totalLaborMale: 0,
          totalProfFemale: 0,
          totalProfMale: 0
        };
      }
      parkGenderData[park].totalFemale += laborFemale + profFemale;
      parkGenderData[park].totalMale += laborMale + profMale;
      parkGenderData[park].totalLaborFemale += laborFemale;
      parkGenderData[park].totalLaborMale += laborMale;
      parkGenderData[park].totalProfFemale += profFemale;
      parkGenderData[park].totalProfMale += profMale;
      
      if (!parkEmployeeTypeData[park]) {
        parkEmployeeTypeData[park] = { 
          totalLabor: 0,
          totalProfessional: 0
        };
      }
      parkEmployeeTypeData[park].totalLabor += laborFemale + laborMale;
      parkEmployeeTypeData[park].totalProfessional += profFemale + profMale;
      
      if (!yearlyEmploymentData[year]) {
        yearlyEmploymentData[year] = {
          totalLabor: 0,
          totalProfessional: 0,
          totalFemale: 0,
          totalMale: 0,
          totalLaborFemale: 0,
          totalLaborMale: 0,
          totalProfFemale: 0,
          totalProfMale: 0,
          totalEmployees: 0
        };
      }
      yearlyEmploymentData[year].totalLabor += laborFemale + laborMale;
      yearlyEmploymentData[year].totalProfessional += profFemale + profMale;
      yearlyEmploymentData[year].totalFemale += laborFemale + profFemale;
      yearlyEmploymentData[year].totalMale += laborMale + profMale;
      yearlyEmploymentData[year].totalLaborFemale += laborFemale;
      yearlyEmploymentData[year].totalLaborMale += laborMale;
      yearlyEmploymentData[year].totalProfFemale += profFemale;
      yearlyEmploymentData[year].totalProfMale += profMale;
      yearlyEmploymentData[year].totalEmployees = yearlyEmploymentData[year].totalLabor + yearlyEmploymentData[year].totalProfessional;
    });
    
    return { parkLaborData, parkProfessionalData, parkGenderData, parkEmployeeTypeData, yearlyEmploymentData };
  }, [filteredActivities]);

  // ADDED: Render function for park charts with toggle between bar and pie
  const renderParkChart = (title, data, dataKey, color, formatValue) => {
    const hasValidData = Object.values(data).some(item => {
      const value = item[dataKey];
      return value && value > 0;
    });
    
    if (!hasValidData) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No data available for {title}
          </Typography>
        </Box>
      );
    }
    
    if (parkChartType === 'pie') {
      const pieData = {};
      Object.entries(data).forEach(([key, val]) => { 
        const value = val[dataKey] || 0;
        if (value > 0) {
          pieData[key] = value;
        }
      });
      
      if (Object.keys(pieData).length === 0) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
          </Box>
        );
      }
      return <PieChart title={title} data={pieData} formatValue={formatValue} />;
    } else {
      return <BarChart title={title} data={data} dataKey={dataKey} color={color} formatValue={formatValue} />;
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Investor ID', 'Company Name', 'Park Name', 'Labor Female', 'Labor Male', 'Professional Female', 'Professional Male', 'Total Labor', 'Total Professional', 'Total Employees', 'Status', 'Year', 'Month'];
    const data = filteredActivities.map(item => [
      item.investor_id,
      item.company_name,
      item.park_name,
      item.emp_labor_female || 0,
      item.emp_labor_male || 0,
      item.emp_prof_female || 0,
      item.emp_prof_male || 0,
      (item.emp_labor_female || 0) + (item.emp_labor_male || 0),
      (item.emp_prof_female || 0) + (item.emp_prof_male || 0),
      (item.emp_labor_female || 0) + (item.emp_labor_male || 0) + (item.emp_prof_female || 0) + (item.emp_prof_male || 0),
      item.status || 'Active',
      item.year,
      item.month
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employment_report_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedPark("all");
    setSelectedStatus("all");
    setSelectedEmploymentType("all");
    setStartYear("all");
    setEndYear("all");
    setStartMonth("all");
    setEndMonth("all");
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const getCurrentPageData = () => { return filteredActivities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage); };
  const handleViewDetails = (activity) => { setSelectedActivity(activity); setOpenDetailsDialog(true); };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        Employment Report Dashboard
      </Typography>
      
      {/* Summary Cards - Reduced Height */}
      <Grid container spacing={2} sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px' }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <PeopleIcon sx={{ color: '#4caf50', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalEmployees)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Total Employees</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0 }}>
                <Typography variant="caption" sx={{ color: '#e91e63', fontSize: '0.55rem', lineHeight: 1 }}>♀ {formatNumber(summaryStats.totalLaborFemale + summaryStats.totalProfFemale)}</Typography>
                <Typography variant="caption" sx={{ color: '#2196f3', fontSize: '0.55rem', lineHeight: 1 }}>♂ {formatNumber(summaryStats.totalLaborMale + summaryStats.totalProfMale)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px' }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <WorkIcon sx={{ color: '#ff9800', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalLabor)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Total Labor Force</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0 }}>
                <Typography variant="caption" sx={{ color: '#e91e63', fontSize: '0.55rem', lineHeight: 1 }}>♀ {formatNumber(summaryStats.totalLaborFemale)}</Typography>
                <Typography variant="caption" sx={{ color: '#2196f3', fontSize: '0.55rem', lineHeight: 1 }}>♂ {formatNumber(summaryStats.totalLaborMale)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px' }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <TrendingUpIcon sx={{ color: '#1976d2', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalProfessional)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Total Professionals</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0 }}>
                <Typography variant="caption" sx={{ color: '#e91e63', fontSize: '0.55rem', lineHeight: 1 }}>♀ {formatNumber(summaryStats.totalProfFemale)}</Typography>
                <Typography variant="caption" sx={{ color: '#2196f3', fontSize: '0.55rem', lineHeight: 1 }}>♂ {formatNumber(summaryStats.totalProfMale)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><ClearIcon sx={{ cursor: 'pointer', fontSize: 'small' }} onClick={() => setSearchTerm("")} /></InputAdornment>)
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Park</InputLabel>
              <Select value={selectedPark} onChange={(e) => setSelectedPark(e.target.value)} label="Park">
                {parksList.map((park) => (<MenuItem key={park} value={park}>{park === "all" ? "All Parks" : park}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                {statusesList.map((status) => (<MenuItem key={status} value={status}>{status === "all" ? "All Status" : status}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Employment Type</InputLabel>
              <Select value={selectedEmploymentType} onChange={(e) => setSelectedEmploymentType(e.target.value)} label="Employment Type">
                {employmentTypes.map((type) => (<MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year From</InputLabel>
              <Select value={startYear} onChange={(e) => setStartYear(e.target.value)} label="Year From">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year To</InputLabel>
              <Select value={endYear} onChange={(e) => setEndYear(e.target.value)} label="Year To">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month From</InputLabel>
              <Select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} label="Month From">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month To</InputLabel>
              <Select value={endMonth} onChange={(e) => setEndMonth(e.target.value)} label="Month To">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={resetFilters} startIcon={<ClearIcon />} size="small" sx={{ textTransform: 'none' }}>
              Reset
            </Button>
          </Grid>
          
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="contained" color="primary" size="small" onClick={handleExportCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
              Export CSV
            </Button>
            <Button variant="outlined" size="small" onClick={() => setShowChart(!showChart)} sx={{ textTransform: 'none' }}>
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Conditional Rendering: Show Chart OR Table */}
      {showChart ? (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant={chartView === 'park' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('park')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />Park-wise View
            </Button>
            <Button variant={chartView === 'yearly' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('yearly')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />Yearly Trend
            </Button>
          </Box>
          
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            {chartView === 'park' ? (
              <>
                {/* ADDED: Chart Type Toggle */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
                    <ToggleButtonGroup value={parkChartType} exclusive onChange={(e, val) => val && setParkChartType(val)} size="small">
                      <ToggleButton value="bar">
                        <Tooltip title="Bar Chart">
                          <BarChartIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="pie">
                        <Tooltip title="Pie Chart">
                          <PieChartIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={parkChartTab} onChange={(e, newValue) => setParkChartTab(newValue)}>
                    <Tab label="Gender Distribution by Park" />
                    <Tab label="Labor vs Professional by Park" />
                    <Tab label="Labor Force by Park" />
                    <Tab label="Professionals by Park" />
                  </Tabs>
                </Box>
                
                <Box sx={{ mt: 2, minHeight: 450 }}>
                  <TabPanel value={parkChartTab} index={0}>
                    <GenderStackedBarChart title="Gender Distribution by Park" parkData={chartData.parkGenderData} />
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={1}>
                    <ProfessionalVsLaborChart title="Labor vs Professional by Park" parkData={chartData.parkEmployeeTypeData} />
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={2}>
                    {renderParkChart("Labor Force by Park", chartData.parkLaborData, "totalLabor", "#4caf50", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={3}>
                    {renderParkChart("Professionals by Park", chartData.parkProfessionalData, "totalProfessional", "#ff9800", (v) => formatNumber(v))}
                  </TabPanel>
                </Box>
              </>
            ) : (
              <YearlyTrendLineChart 
                title="Yearly Employment Trends" 
                yearlyData={chartData.yearlyEmploymentData} 
                dataKeys={['totalLabor', 'totalProfessional', 'totalFemale', 'totalMale']}
                colors={['#4caf50', '#ff9800', '#e91e63', '#2196f3']}
                formatValue={(v) => formatNumber(v)}
              />
            )}
          </Paper>
        </>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading employment data...</Typography>
            </Box>
          ) : filteredActivities.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Typography sx={{ color: 'text.secondary' }}>No employment data available</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Investor ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Park Name</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#e91e63' }}>Labor (F)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#2196f3' }}>Labor (M)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#e91e63' }}>Prof (F)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#2196f3' }}>Prof (M)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Labor</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Prof</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Emp</TableCell>
                      
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getCurrentPageData().map((row) => {
                      const totalLabor = (row.emp_labor_female || 0) + (row.emp_labor_male || 0);
                      const totalProfessional = (row.emp_prof_female || 0) + (row.emp_prof_male || 0);
                      const totalEmployees = totalLabor + totalProfessional;
                      
                      const monthNames = {
                        "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr", "05": "May", "06": "Jun",
                        "07": "Jul", "08": "Aug", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec"
                      };
                      
                      return (
                        <TableRow key={row.id || row.investor_id} hover>
                          <TableCell>{row.investor_id}</TableCell>
                          <TableCell sx={{ fontWeight: 'medium' }}>{row.company_name}</TableCell>
                          <TableCell>{row.park_name}</TableCell>
                          <TableCell align="center" sx={{ color: '#e91e63' }}>{formatNumber(row.emp_labor_female)}</TableCell>
                          <TableCell align="center" sx={{ color: '#2196f3' }}>{formatNumber(row.emp_labor_male)}</TableCell>
                          <TableCell align="center" sx={{ color: '#e91e63' }}>{formatNumber(row.emp_prof_female)}</TableCell>
                          <TableCell align="center" sx={{ color: '#2196f3' }}>{formatNumber(row.emp_prof_male)}</TableCell>
                          <TableCell align="right">{formatNumber(totalLabor)}</TableCell>
                          <TableCell align="right">{formatNumber(totalProfessional)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                            {formatNumber(totalEmployees)}
                          </TableCell>
                          
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination 
                rowsPerPageOptions={[5, 10, 25, 50]} 
                component="div" 
                count={filteredActivities.length} 
                rowsPerPage={rowsPerPage} 
                page={page} 
                onPageChange={handleChangePage} 
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </>
          )}
        </Paper>
      )}
      
      {/* Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Employment Activity Details
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedActivity && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Investor ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedActivity.investor_id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Company Name</Typography>
                  <Typography variant="body1">{selectedActivity.company_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Park Name</Typography>
                  <Typography variant="body1">{selectedActivity.park_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Status</Typography>
                  <Chip label={selectedActivity.status || 'Active'} size="small" sx={getStatusColor(selectedActivity.status)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Recorded On</Typography>
                  <Typography variant="body1">{selectedActivity.recorded_on ? new Date(selectedActivity.recorded_on).toLocaleDateString() : 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Activity Period</Typography>
                  <Typography variant="body1">
                    {selectedActivity.activity_period_start_from ? new Date(selectedActivity.activity_period_start_from).toLocaleDateString() : 'N/A'} 
                    {' - '}
                    {selectedActivity.activity_period_to ? new Date(selectedActivity.activity_period_to).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>Employment Breakdown</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fce4ec' }}>
                    <Typography variant="subtitle2" sx={{ color: '#c62828', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FemaleIcon /> Female Employees
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {formatNumber((selectedActivity.emp_labor_female || 0) + (selectedActivity.emp_prof_female || 0))}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption">Labor: {formatNumber(selectedActivity.emp_labor_female)}</Typography>
                      <Typography variant="caption">Professional: {formatNumber(selectedActivity.emp_prof_female)}</Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    <Typography variant="subtitle2" sx={{ color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MaleIcon /> Male Employees
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {formatNumber((selectedActivity.emp_labor_male || 0) + (selectedActivity.emp_prof_male || 0))}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption">Labor: {formatNumber(selectedActivity.emp_labor_male)}</Typography>
                      <Typography variant="caption">Professional: {formatNumber(selectedActivity.emp_prof_male)}</Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Summary</Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Total Labor</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {formatNumber((selectedActivity.emp_labor_female || 0) + (selectedActivity.emp_labor_male || 0))}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Total Professional</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {formatNumber((selectedActivity.emp_prof_female || 0) + (selectedActivity.emp_prof_male || 0))}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Total Employees</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                          {formatNumber((selectedActivity.emp_labor_female || 0) + (selectedActivity.emp_labor_male || 0) + 
                                       (selectedActivity.emp_prof_female || 0) + (selectedActivity.emp_prof_male || 0))}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                
                {selectedActivity.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: '#666' }}>Description</Typography>
                    <Typography variant="body2">{selectedActivity.description}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
    </Box>
  );
}